"use client";

import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/auth-context";
import { getEmailValidationError } from "@/lib/emailValidation";

interface SuggestCertificationFormProps {
  initialName?: string;
  onSuccess?: () => void;
  submitLabel?: string;
}

export function SuggestCertificationForm({
  initialName = "",
  onSuccess,
  submitLabel = "Suggest this certification",
}: SuggestCertificationFormProps) {
  const { isAuthenticated, isLoading } = useAuth();
  const [name, setName] = useState(initialName);
  const [organization, setOrganization] = useState("");
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    setName(initialName);
  }, [initialName]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedName = name.trim();
    if (!trimmedName) {
      setError("Please enter the certification name.");
      return;
    }

    if (isLoading) return;

    const trimmedEmail = email.trim();
    if (!isAuthenticated) {
      const emailError = getEmailValidationError(trimmedEmail);
      if (!trimmedEmail || emailError) {
        setError(emailError || "Please enter a valid email address.");
        return;
      }
    }

    setError("");
    setIsSubmitting(true);
    try {
      const res = await fetch("/api/certification-request", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: trimmedName,
          organization: organization.trim() || undefined,
          ...(!isAuthenticated ? { email: trimmedEmail } : {}),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        setError(data.error || "Failed to submit suggestion. Please try again.");
        return;
      }
      onSuccess?.();
    } catch {
      setError("Failed to submit suggestion. Please try again later.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="suggest-cert-name">Certification name</Label>
        <Input
          id="suggest-cert-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. AWS Certified Solutions Architect"
          disabled={isSubmitting}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="suggest-cert-org">
          Certifying organization{" "}
          <span className="font-normal text-gray-500">(optional)</span>
        </Label>
        <Input
          id="suggest-cert-org"
          value={organization}
          onChange={(e) => setOrganization(e.target.value)}
          placeholder="e.g. Amazon Web Services"
          disabled={isSubmitting}
        />
      </div>
      {!isAuthenticated && !isLoading && (
        <div className="space-y-2">
          <Label htmlFor="suggest-cert-email">Your email address</Label>
          <Input
            id="suggest-cert-email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              const next = e.target.value.trim();
              setError(next ? getEmailValidationError(next) : "");
            }}
            placeholder="you@example.com"
            disabled={isSubmitting}
          />
          <p className="text-xs text-gray-500">
            We&apos;ll use this to follow up if we add this certification.
          </p>
        </div>
      )}
      {error && <p className="text-sm text-red-600">{error}</p>}
      <Button type="submit" className="w-full" disabled={isSubmitting || isLoading}>
        {isSubmitting ? "Submitting…" : submitLabel}
      </Button>
    </form>
  );
}
